import React from 'react';
import { CheckCircle2Icon } from 'lucide-react';
import { Liability } from '../types/credit';
import { formatCurrency } from '../data/reports';
import { StatusBadge } from './StatusBadge';
type LiabilityTableProps = {
  liabilities: Liability[];
  pendingCount: number;
  totalBalance: number;
  totalMonthly: number;
};
export function LiabilityTable({
  liabilities,
  pendingCount,
  totalBalance,
  totalMonthly
}: LiabilityTableProps) {
  return <div className="overflow-hidden rounded-lg border border-ink-100 bg-white shadow-sm" data-id="element-33" data-mp="1wd34575a47zq">
      <div className="flex flex-col gap-1 border-b border-ink-100 px-4 py-3 sm:flex-row sm:items-center sm:justify-between" data-id="element-34" data-mp="21av50qnwj4g2">
        <p className="text-sm text-ink-500" data-id="element-35" data-mp="25e9ojrb1u2y9">
          <span className="font-medium text-ink-900" data-id="element-36" data-mp="2ac2ssesskmvw">
            {liabilities.length} liabilities
          </span>{' '}
          on this report
        </p>
        <p className="text-sm text-ink-500" data-id="element-37" data-mp="3lq6lzirk26zw">
          {pendingCount > 0 ? `All ${pendingCount} new ${pendingCount === 1 ? 'account' : 'accounts'} will be pushed together` : 'Everything on this report is already in Encompass'}
        </p>
      </div>

      <div className="overflow-x-auto" data-id="element-38" data-mp="3s81fei1j26kg">
        <table className="w-full min-w-[720px] text-left text-sm" data-id="element-39" data-mp="3n7x3747njy01">
          <caption className="sr-only" data-id="element-40" data-mp="3na9o9q8piohb">
            Liabilities from the selected credit report
          </caption>
          <thead className="bg-ink-50 text-xs uppercase tracking-wide text-ink-500" data-id="element-41" data-mp="3e7idlsnnnwqq">
            <tr data-id="element-42" data-mp="3e9bltcdx2p0u">
              <th scope="col" className="px-4 py-3 font-semibold" data-id="element-43" data-mp="39h1z0583m52s">
                Creditor
              </th>
              <th scope="col" className="w-32 px-4 py-3 font-semibold" data-id="element-44" data-mp="34h5tg1jr65l4">
                Type
              </th>
              <th scope="col" className="w-32 px-4 py-3 font-semibold" data-id="element-45" data-mp="2zjdtqlcv9f6d">
                Owner
              </th>
              <th scope="col" className="w-32 px-4 py-3 font-semibold" data-id="element-46" data-mp="2ui7x4cg357rk">
                Status
              </th>
              <th scope="col" className="w-32 px-4 py-3 text-right font-semibold" data-id="element-47" data-mp="2qifwclwxo5wr">
                Balance
              </th>
              <th scope="col" className="w-28 px-4 py-3 text-right font-semibold" data-id="element-48" data-mp="2kw5khq34bjx1">
                Monthly
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-ink-100" data-id="element-49" data-mp="2bw8rxddly0wp">
            {liabilities.map(liability => <tr key={liability.id} className={liability.alreadyPushed ? 'bg-ink-50/70' : undefined} data-id="element-50" data-mp="2zm34dtu0lngf">
                <td className="px-4 py-3.5 align-middle" data-id="element-51" data-mp="2pv04gvctneiq">
                  <div className="font-medium leading-tight text-ink-900" data-id="element-52" data-mp="2pugwa6cz44hw">
                    {liability.creditor}
                  </div>
                  <div className="mt-0.5 flex items-center gap-2 text-xs leading-tight text-ink-500" data-id="element-53" data-mp="3ijnu134lsq23">
                    {liability.accountNumber}
                    {liability.alreadyPushed && <span className="inline-flex items-center gap-1 font-medium text-green-700" data-id="element-54" data-mp="js7pa1yg0ew">
                        <CheckCircle2Icon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-55" data-mp="3snkp8811t4o6" />
                        In Encompass
                      </span>}
                  </div>
                </td>
                <td className="px-4 py-3.5 align-middle text-ink-700" data-id="element-56" data-mp="5cuw73rvkjo8">
                  {liability.accountType}
                </td>
                <td className="px-4 py-3.5 align-middle text-ink-700" data-id="element-57" data-mp="39pi8shmkgg1z">
                  {liability.owner}
                </td>
                <td className="px-4 py-3.5 align-middle" data-id="element-58" data-mp="3nq2r7cokk9uy">
                  <StatusBadge status={liability.status} data-id="element-59" data-mp="3iovjteqc9cot" />
                </td>
                <td className="px-4 py-3.5 text-right align-middle tabular-nums text-ink-900" data-id="element-60" data-mp="12yaddq4qjkle">
                  {formatCurrency(liability.balance)}
                </td>
                <td className="px-4 py-3.5 text-right align-middle tabular-nums text-ink-900" data-id="element-61" data-mp="xyxsvc1c6plz">
                  {formatCurrency(liability.monthlyPayment)}
                </td>
              </tr>)}
          </tbody>
          <tfoot className="border-t border-ink-100 bg-ink-50 text-sm" data-id="element-62" data-mp="3dlnnlkowj6qx">
            <tr data-id="element-63" data-mp="3dkckzo77051e">
              <td colSpan={4} className="px-4 py-3 font-medium text-ink-700" data-id="element-64" data-mp="3do0dck0wilej">
                Report totals
              </td>
              <td className="px-4 py-3 text-right font-semibold tabular-nums text-ink-900" data-id="element-65" data-mp="3mlssno6mht1x">
                {formatCurrency(totalBalance)}
              </td>
              <td className="px-4 py-3 text-right font-semibold tabular-nums text-ink-900" data-id="element-66" data-mp="67w7327sr48">
                {formatCurrency(totalMonthly)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>;
}